import { useContext, useEffect, useState } from "react";

import { Badge, Button, Card, TextInput } from "flowbite-react";

import { Actions } from "../reducers/Booking";
import Stepper, { STEPS } from "../components/Stepper";

import rtc from "../services/rtc";

import { FaCar, FaRoute, FaTimes } from "react-icons/fa";
import AppContext from "../context/AppContext";

const ITEMS_PER_PAGE = 10;

function titleCase(str) {
  return str
    .toLowerCase()
    .split(" ")
    .map((v) => v.charAt(0).toUpperCase() + v.substring(1))
    .join(" ");
}

const Page = ({ state, dispatch, pushHash }) => {
  const {
    brand: {
      buttonBorderColor,
      buttonBackgroundColor,
      buttonTextColor,

      inputBorderColor,
      inputBackgroundColor,
      inputTextColor,

      buttonPrimaryBorderColor,
      buttonPrimaryBackgroundColor,
      buttonPrimaryTextColor,
    },
  } = useContext(AppContext);

  const [authorizedDealers, setAuthorizedDealers] = useState(null);
  const [otherDealers, setOtherDealers] = useState(null);

  const [limit, setLimit] = useState(ITEMS_PER_PAGE);

  const [postcode, setPostcode] = useState(
    state.customer ? state.customer.postcode || "" : ""
  );

  useEffect(() => {
    setLimit(ITEMS_PER_PAGE);
  }, [state]);

  useEffect(() => {
    rtc
      .getDealers(postcode, "asc")
      .then((res) => {
        const make = (state.vehicle ? state.vehicle.make || "" : "")
          .toLowerCase()
          .replace(/[^a-z0-9-]/g, "");

        setAuthorizedDealers(
          make
            ? res.filter((v) =>
                (v.marque || "")
                  .toLowerCase()
                  .replace(/[^a-zA-Z0-9-,|,. ]/g, "")
                  .split(/[|,. ]/)
                  .filter((v) => !!v)
                  .includes(make)
              )
            : []
        );
        setOtherDealers(
          make
            ? res.filter(
                (v) =>
                  !(v.marque || "")
                    .toLowerCase()
                    .replace(/[^a-zA-Z0-9-,|,. ]/g, "")
                    .split(/[|,. ]/)
                    .filter((v) => !!v)
                    .includes(make)
              )
            : res
        );
      })
      .catch((err) => console.error(err));
  }, [postcode, state]);

  const selectDealer = (dealer) => {
    dispatch({
      type: Actions.SetDealer,
      payload: dealer,
    });
  };

  return (
    <>
      <Stepper step={STEPS.Appointment} pushHash={pushHash} />

      <h2 className="text-xl block ">
        Please select your preferred service location
      </h2>

      <div className="flex flex-row py-4 gap-4">
        <span className="py-2">Sort by distance from postcode</span>
        <div>
          <TextInput
            className="w-48"
            id="customer-postcode"
            defaultValue={postcode}
            onBlur={(evt) => setPostcode(evt.target.value)}
            rightIcon={() => (
              <FaTimes
                className="pointer-events-auto cursor-pointer"
                onMouseUp={() => {
                  document.getElementById("customer-postcode").value = "";
                  setTimeout(() => setPostcode(""), 100);
                }}
              />
            )}
            style={{
              borderColor: inputBorderColor,
              backgroundColor: inputBackgroundColor,
              color: inputTextColor,
            }}
          />
        </div>
      </div>
      {Array.isArray(authorizedDealers) && authorizedDealers.length > 0 && (
        <>
          <h2 className="text-xl font-bold mb-2">
            Authorised {titleCase(state.vehicle.make)} Service Centres
          </h2>
          {authorizedDealers
            .filter((_, idx) => idx < limit)
            .map((d) => (
              <Card className="mb-8 md:m-auto md:mb-8" key={d.dealerId}>
                <div className="flex flex-row justify-between">
                  <h5 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                    <span title={`${d.description} (${d.dealerId})`}>
                      {d.description}
                    </span>
                  </h5>
                  {Number.isFinite(d.distance) && (
                    <span className="nowrap pt-2">
                      <FaRoute className="inline" />{" "}
                      {d.distance === 0
                        ? "in the neighborhood"
                        : `${d.distance} miles away`}
                    </span>
                  )}
                </div>

                <p>
                  {(d.marque || "")
                    .replace(/[^a-zA-Z0-9-,|,. ]/g, "")
                    .split(/[|,. ]/)
                    .filter((v) => !!v)
                    .map((m) => (
                      <Badge
                        key={m}
                        size="xs"
                        color="gray"
                        className="inline-block mr-1"
                        style={{
                          borderColor: buttonBorderColor,
                          backgroundColor: buttonBackgroundColor,
                          color: buttonTextColor,
                        }}
                      >
                        {m}
                      </Badge>
                    ))}
                </p>

                <Button
                  type="button"
                  color="dark"
                  className="w-full"
                  style={{
                    borderColor: buttonPrimaryBorderColor,
                    backgroundColor: buttonPrimaryBackgroundColor,
                    color: buttonPrimaryTextColor,
                  }}
                  onClick={() => selectDealer(d)}
                >
                  Select
                </Button>
              </Card>
            ))}

          {Array.isArray(otherDealers) && otherDealers.length > 0 && (
            <h2 className="text-xl font-bold mb-2">Other Service Centres</h2>
          )}
        </>
      )}

      {Array.isArray(otherDealers) && otherDealers.length > 0 && (
        <>
          {otherDealers
            .filter((_, idx) => idx < limit)
            .map((d) => (
              <Card className="mb-8 md:m-auto md:mb-8" key={d.dealerId}>
                <div className="flex flex-row justify-between">
                  <h5 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                    <span title={`${d.description} (${d.dealerId})`}>
                      {d.description}
                    </span>
                  </h5>
                  {Number.isFinite(d.distance) && (
                    <span className="nowrap pt-2">
                      <FaRoute className="inline" />{" "}
                      {d.distance === 0
                        ? "in the neighborhood"
                        : `${d.distance} miles away`}
                    </span>
                  )}
                </div>

                <p>
                  {(d.marque || "")
                    .replace(/[^a-zA-Z0-9-,|,. ]/g, "")
                    .split(/[|,. ]/)
                    .filter((v) => !!v)
                    .map((m) => (
                      <Badge
                        key={m}
                        size="xs"
                        color="gray"
                        className="inline-block mr-1"
                        style={{
                          borderColor: buttonBorderColor,
                          backgroundColor: buttonBackgroundColor,
                          color: buttonTextColor,
                        }}
                      >
                        {m}
                      </Badge>
                    ))}
                </p>

                <Button
                  type="button"
                  color="dark"
                  className="w-full"
                  style={{
                    borderColor: buttonPrimaryBorderColor,
                    backgroundColor: buttonPrimaryBackgroundColor,
                    color: buttonPrimaryTextColor,
                  }}
                  onClick={() => selectDealer(d)}
                >
                  Select
                </Button>
              </Card>
            ))}

          {limit < otherDealers.length && (
            <Button
              type="button"
              color="gray"
              className="m-auto"
              style={{
                borderColor: buttonBorderColor,
                backgroundColor: buttonBackgroundColor,
                color: buttonTextColor,
              }}
              onClick={() => setLimit((l) => l + ITEMS_PER_PAGE)}
            >
              Show more dealerships
            </Button>
          )}
        </>
      )}
    </>
  );
};

const Description = ({ state }) => {
  const {
    brand: { textColor, textShadeColor },
  } = useContext(AppContext);

  return (
    <>
      <h2 className="text-4xl font-bold" style={{ color: textColor }}>
        Your booking
      </h2>

      <div className="pb-4 pt-8" style={{ color: textColor }}>
        <FaCar size="2em" />
      </div>
      <h3 className="text-2xl font-bold pb-4" style={{ color: textColor }}>
        Your Vehicle
      </h3>
      <p className="text-xl text-slate-500" style={{ color: textShadeColor }}>
        {state.vehicle.regnum}
      </p>
      <p className="text-xl text-slate-500" style={{ color: textShadeColor }}>
        {state.vehicle.description}
      </p>
    </>
  );
};

const tuple = { Page, Description };

export default tuple;
